import { useRef, useState, useEffect } from 'react';
import { UploadFileButton } from '../UploadFileButton/UploadFileButton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import styles from './ImageUploaderAndResizer.module.css';
import { useImageHelper } from '../../../../Hooks/ImageHelper.hook';
import { ImageResizerAndFields } from '../ImageResizerAndFields/ImageResizerAndFields';
import { useCmAndPixelsConverter } from '../../../../Hooks/CmAndPixelsConverter.hook';
import { toast } from 'react-toastify';

const defaultAspectRatio = 1;

export function ImageUploaderAndResizer({ ...props }) {
    const { unitToCm } = useCmAndPixelsConverter();
    const [aspectRatio, setAspectRatio] = useState(props.aspectRatio || defaultAspectRatio);
    const { getImageInBase64, normalyzeToBase64, getImageDimensions } = useImageHelper();
    const uploadButtonRef = useRef(null);

    useEffect(() => {
        if (props.width && props.height && props.width > 0 && props.height > 0) {
            const newAspectRatio = props.width / props.height;
            setAspectRatio(newAspectRatio);
        }
    }, [props.width, props.height, props.imageSrc]);

    const setUploadedImageInBase64 = async (file) => {
        const dimensions = { width: 1, height: 1 };

        try {
            const { width, height } = await getImageDimensions(file);
            dimensions.width = width;
            dimensions.height = height;
        } catch (error) {
            console.error(error);
        }

        const aspectRatio = dimensions.width / dimensions.height;
        const width = Math.min(unitToCm(dimensions.width), props.maxWidth);
        const height = width / aspectRatio;

        setAspectRatio(aspectRatio);
        props.onChangeWidth(width);
        props.onChangeHeight(height);
    };

    const validateImageFile = (file, allowedTypes = []) => {
        if (!file || !allowedTypes.length) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            return 'Invalid image file. Try another one';
        }

        if (!allowedTypes.includes(file.type)) {
            return `This image type is not allowed. Accepted types: ${allowedTypes.join(', ')}`;
        }
    };

    const handleChangeUploadingFile = async (file) => {
        const acceptedTypes = props.acceptedTypes;

        if (file && acceptedTypes) {
            const validationError = validateImageFile(file, acceptedTypes);
            if (validationError) {
                toast.error(validationError);
                return;
            }
        }

        props.onChangeImage(null);

        if (file) {
            const imageBase64 = await getImageInBase64(file)
                .catch(console.error);
            const base64WithoutPrefix = imageBase64.substring(imageBase64.indexOf(',') + 1);

            file['base64'] = base64WithoutPrefix;
            props.onChangeImage(base64WithoutPrefix);
            await setUploadedImageInBase64(file);
        }
    };

    const imageInBase64 = normalyzeToBase64(props.imageSrc || '');
    const hasImage = !!props.imageSrc;

    const handleDeleteImage = () => {
        if (props.onDelete) {
            props.onDelete();
        }

        if (uploadButtonRef.current) {
            uploadButtonRef.current.clearInput();
        }
    };

    return (
        <div className={styles.uploadAndPreviewArea}>
            <div className={styles.imageErrorAndPreview}>
                <div className={styles.uploadButtonContainer}>
                    <UploadFileButton
                        ref={uploadButtonRef}
                        error={props.isImageError}
                        helperText={props.imageHelperText}
                        label={props.uploadButtonLabel}
                        onChange={handleChangeUploadingFile}
                        accept={props.acceptedTypes}
                    />
                    {hasImage && props.onDelete && (
                        <Tooltip title="Remove image">
                            <IconButton
                                className={styles.deleteImageButton}
                                onClick={handleDeleteImage}
                                color="error"
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
                {
                    props.isImageError && (
                        <p className={styles.imageErrorText}>{props.imageHelperText}</p>
                    )
                }
            </div>
            <ImageResizerAndFields
                required={props.required}
                imageInBase64={imageInBase64}
                width={props.width}
                height={props.height}
                inputUnit={props.inputUnit}
                outputUnit={props.outputUnit}
                aspectRatio={aspectRatio}
                isAspectRatioIgnored={aspectRatio === defaultAspectRatio}
                onChangeWidth={props.onChangeWidth}
                onChangeHeight={props.onChangeHeight}
                isWidthError={props.isWidthError}
                isHeightError={props.isHeightError}
                widthFieldLabel={props.widthFieldLabel}
                heightFieldLabel={props.heightFieldLabel}
                imageHelperText={props.imageHelperText}
                widthHelperText={props.widthHelperText}
                heightHelperText={props.heightHelperText}
                minWidth={props.minWidth}
                maxWidth={props.maxWidth}
                step={props.step}
            />
        </div>
    );
}
